// WHAT: Settings modal component for configuring Ollama model preferences and connection settings
// WHY:  Provides interface for users to manage default model selection and view connection information
// DOMAIN: LLM → Ollama → Settings Management
// DEPENDENCIES: React state management, UI components, modal backdrop handling
// RISKS:
//   - NOTE: Model change tracking uses local state - may not sync with parent if modal closes unexpectedly
//   - NOTE: Connection info is hardcoded - may not reflect actual Ollama server configuration
//   - WARN: Modal backdrop click closes without saving - may cause data loss
//   - NOTE: No validation for model selection - may allow invalid model names

import { X, Bot, Save, RotateCcw } from "lucide-react";
import { type FC, useState } from "react";

import { Button, Stack, Text, Heading, Card } from "@/components/ui";

/**
 * WHAT:
 *   Props interface for OllamaSettingsModal component.
 */
interface OllamaSettingsModalProperties {
  /** List of available models */
  availableModels: string[];
  /** Currently active model */
  currentModel: string;
  /** Whether modal is visible */
  isOpen: boolean;
  /** Callback to close modal */
  onClose: () => void;
  /** Callback when model is changed */
  onModelChange: (model: string) => void;
}

/**
 * WHAT:
 *   Modal component for configuring Ollama model preferences and connection settings.
 *
 * WHY:
 *   Provides interface for users to manage default model selection and view connection information.
 *
 * PROPS:
 *   - isOpen: boolean — Whether modal is visible
 *   - onClose: () => void — Callback to close modal
 *   - availableModels: string[] — List of available models
 *   - currentModel: string — Currently active model
 *   - onModelChange: (model: string) => void — Callback when model is changed
 *
 * BEHAVIOR:
 *   - Displays modal overlay with backdrop
 *   - Shows list of available models with radio button selection
 *   - Tracks changes locally before saving
 *   - Provides reset functionality to discard changes
 *   - Shows connection information (endpoint, model count)
 *   - Displays info box about model change behavior
 *   - Closes on backdrop click or cancel button
 *
 * RISKS:
 *   - NOTE: Model change tracking uses local state - may not sync with parent if modal closes unexpectedly
 *   - NOTE: Connection info is hardcoded - may not reflect actual Ollama server configuration
 *   - WARN: Modal backdrop click closes without saving - may cause data loss
 *   - NOTE: No validation for model selection - may allow invalid model names
 *
 * @param root0
 *
 * @param root0.isOpen
 *
 * @param root0.onClose
 *
 * @param root0.availableModels
 *
 * @param root0.currentModel
 *
 * @param root0.onModelChange
 *
 * @returns JSX element with settings modal or null if not open
 *
 */
export const OllamaSettingsModal: FC<OllamaSettingsModalProperties> = ({
  availableModels,
  currentModel,
  isOpen,
  onClose,
  onModelChange,
}) => {
  const safeAvailableModels = Array.isArray(availableModels) ? availableModels : [];
  const [selectedModel, setSelectedModel] = useState<string>(
    typeof currentModel === "string" ? currentModel : "",
  );
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  if (!isOpen) {
    return null;
  }

  /**
   * WHAT:
   *   Saves selected model changes and closes modal.
   *
   * WHY:
   *   Applies model selection only if it differs from current model.
   *
   * SIDE EFFECTS:
   *   - Calls onModelChange if model changed
   *   - Closes modal via onClose callback
   */
  const handleSave = () => {
    if (selectedModel !== currentModel) {
      onModelChange(selectedModel);
    }
    onClose();
  };

  /**
   * WHAT:
   *   Resets local model selection to current model and clears change tracking.
   *
   * WHY:
   *   Allows users to discard unsaved changes.
   */
  const handleReset = () => {
    setSelectedModel(typeof currentModel === "string" ? currentModel : "");
    setHasChanges(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-slate-800 border-slate-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <Stack align="center" direction="row" gap={3}>
              <Bot className="w-6 h-6 text-blue-400" />
              <Heading className="text-xl font-semibold text-white" level={3}>
                Ollama Settings
              </Heading>
            </Stack>
            <Button className="gap-2" onClick={onClose} size="sm" variant="ghost">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <Stack direction="col" gap={6}>
              {/* Model Selection */}
              <div>
                <Text className="text-sm font-semibold text-white mb-3">Default Model</Text>
                <Text className="text-xs text-slate-400 mb-4">
                  Choose the model that will be used by default for new conversations
                </Text>

                <div className="grid grid-cols-1 gap-2">
                  {safeAvailableModels.map((model) => {
                    const isSelected = model === selectedModel;
                    return (
                      <button
                        className={`
                          px-4 py-3 rounded-lg border transition-all text-left
                          ${
                            isSelected
                              ? "border-blue-500 bg-blue-500/10"
                              : "border-slate-700 hover:border-slate-600 bg-slate-900/50"
                          }
                        `}
                        key={model}
                        onClick={() => {
                          setSelectedModel(model);
                          setHasChanges(model !== currentModel);
                        }}
                      >
                        <Stack align="center" direction="row" gap={3}>
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                              ${isSelected ? "border-blue-500" : "border-slate-600"}
                            `}
                          >
                            {isSelected ? (
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                            ) : null}
                          </div>
                          <div className="flex-1">
                            <Text className="text-sm font-medium text-white">{model}</Text>
                            {model === currentModel && !isSelected && (
                              <Text className="text-xs text-slate-500 mt-0.5">
                                Currently active
                              </Text>
                            )}
                          </div>
                        </Stack>
                      </button>
                    );
                  })}
                </div>

                {safeAvailableModels.length === 0 && (
                  <Card className="p-4 bg-slate-900 border-slate-700">
                    <Text className="text-sm text-slate-400 text-center">
                      No models installed. Run:{" "}
                      <code className="px-2 py-1 bg-slate-800 rounded">
                        ollama pull deepseek-r1:8b
                      </code>
                    </Text>
                  </Card>
                )}
              </div>

              {/* Connection Info */}
              <div>
                <Text className="text-sm font-semibold text-white mb-3">Connection</Text>
                <Card className="p-4 bg-slate-900 border-slate-700">
                  <Stack direction="col" gap={2}>
                    <div className="flex justify-between">
                      <Text className="text-xs text-slate-400">Endpoint</Text>
                      <Text className="text-xs text-white font-mono">localhost:11434</Text>
                    </div>
                    <div className="flex justify-between">
                      <Text className="text-xs text-slate-400">Available Models</Text>
                      <Text className="text-xs text-white">{safeAvailableModels.length}</Text>
                    </div>
                  </Stack>
                </Card>
              </div>

              {/* Info Box */}
              <Card className="p-4 bg-blue-500/5 border-blue-500/20">
                <Text className="text-xs text-blue-300">
                  <strong>Note:</strong> Model changes will apply to new conversations. Existing
                  conversations will continue using their original model.
                </Text>
              </Card>
            </Stack>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between bg-slate-800/50">
            <Button
              className="gap-2"
              disabled={!hasChanges}
              onClick={handleReset}
              size="sm"
              variant="ghost"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>

            <Stack direction="row" gap={2}>
              <Button onClick={onClose} size="sm" variant="outline">
                Cancel
              </Button>
              <Button className="gap-2" onClick={handleSave} size="sm" variant="primary">
                <Save className="w-4 h-4" />
                Save Changes
              </Button>
            </Stack>
          </div>
        </Card>
      </div>
    </>
  );
};
